<div class="coaching-layout">
  <!-- Sidebar -->
  <aside
    class="sidebar"
    [class.collapsed]="isSidebarCollapsed"
    [class.mobile-open]="isMobileMenuOpen"
  >
    <div class="sidebar-header">
      <div class="logo">
        <i class="fas fa-graduation-cap logo-icon"></i>
        <span class="logo-text" *ngIf="!isSidebarCollapsed">{{ appInfo.name }}</span>
      </div>
      <button class="sidebar-toggle desktop-only" (click)="toggleSidebar()">
        <i
          class="fas"
          [class.fa-chevron-left]="!isSidebarCollapsed"
          [class.fa-chevron-right]="isSidebarCollapsed"
        ></i>
      </button>
    </div>

    <nav class="sidebar-nav">
      <ul class="nav-list">
        <li class="nav-item" *ngFor="let route of visibleRoutes$ | async; trackBy: trackByFn">
          <a [routerLink]="route.path" routerLinkActive="active" class="nav-link">
            <i [class]="route.iconClass + ' nav-icon'" *ngIf="route.iconClass"></i>
            <span class="nav-text" *ngIf="!isSidebarCollapsed">{{ route.name | abpLocalization }}</span>
          </a>
        </li>
      </ul>
    </nav>

    <div class="sidebar-footer">
      <div class="user-info" *ngIf="(currentUser$ | async)?.isAuthenticated">
        <div class="user-avatar">
          <i class="fas fa-user-circle"></i>
        </div>
        <div class="user-details" *ngIf="!isSidebarCollapsed && (currentUser$ | async) as user">
          <span class="user-name">{{ user.userName }}</span>
          <span class="user-role">{{ user.roles[0]}}</span>
        </div>
      </div>
      <button
        class="logout-btn"
        *ngIf="(currentUser$ | async)?.isAuthenticated"
        (click)="onLogout()"
        [attr.title]="'::Layout:Logout' | abpLocalization">
        <i class="fas fa-sign-out-alt"></i>
        <span *ngIf="!isSidebarCollapsed">{{ '::Layout:Logout' | abpLocalization }}</span>
      </button>
      <button
        class="logout-btn"
        *ngIf="!(currentUser$ | async)?.isAuthenticated"
        (click)="navigateToLogin()"
        [attr.title]="'::Layout:Login' | abpLocalization">
        <i class="fas fa-sign-in-alt"></i>
        <span *ngIf="!isSidebarCollapsed">{{ '::Layout:Login' | abpLocalization }}</span>
      </button>
    </div>
  </aside>

  <!-- Main Content -->
  <div class="main-wrapper" [class.sidebar-collapsed]="isSidebarCollapsed">
    <!-- Top Bar -->
    <header class="topbar">
      <button class="mobile-menu-toggle" (click)="toggleMobileMenu()">
        <i class="fas fa-bars"></i>
      </button>

      <div class="topbar-left">
        <h1 class="page-title">{{ (pageTitle$ | async) | abpLocalization }}</h1>
      </div>

      <div class="topbar-right">
        <!-- Dynamic Notifications -->
        <div class="notifications">
          <button class="notification-btn" (click)="toggleNotificationDropdown()" [class.active]="isNotificationDropdownOpen">
            <i class="fas fa-bell"></i>
            <span class="notification-badge" *ngIf="hasUnreadNotifications">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
          </button>

          <div class="notification-dropdown" [class.show]="isNotificationDropdownOpen">
            <div class="dropdown-header">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">{{ '::Notifications' | abpLocalization }}</h6>
                <button class="btn btn-sm btn-link text-decoration-none p-0" (click)="markAllAsRead()" *ngIf="hasUnreadNotifications">
                  <i class="fas fa-check-double me-1"></i>
                  {{ '::MarkAllAsRead' | abpLocalization }}
                </button>
              </div>
            </div>
            <div class="dropdown-body">
              <!-- Dynamic Notifications -->
              <div *ngFor="let notification of dynamicNotifications; trackBy: trackByFn"
                   class="notification-item"
                   [class.unread]="!notification.read"
                   (click)="navigateToNotification(notification); $event.stopPropagation()">
                <div class="notification-icon" [ngClass]="getNotificationColor(notification.type)">
                  <i [class]="getNotificationIcon(notification.type)"></i>
                </div>
                <div class="notification-content flex-grow-1">
                  <h6 class="notification-title">{{ notification.title }}</h6>
                  <p class="notification-text">{{ notification.description }}</p>
                  <small class="notification-time text-muted">{{ notification.time }}</small>
                </div>
                <div class="notification-actions">
                  <button class="btn btn-sm btn-link text-muted p-1"
                          (click)="removeNotification(notification.id); $event.stopPropagation()"
                          title="Remove">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- No notifications message -->
              <div class="no-notifications" *ngIf="dynamicNotifications.length === 0">
                <div class="text-center py-4">
                  <i class="fas fa-bell-slash text-muted mb-2" style="font-size: 2rem;"></i>
                  <p class="text-muted mb-0">{{ '::NoNewNotifications' | abpLocalization }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- User Menu -->
        <div class="user-menu" *ngIf="(currentUser$ | async)?.isAuthenticated">
          <button class="user-menu-trigger" (click)="toggleUserMenu()" [class.active]="isUserMenuOpen">
            <i class="fas fa-user-circle"></i>
            <span class="user-name desktop-only">{{ (currentUser$ | async)?.userName }}</span>
            <i class="fas fa-chevron-down dropdown-icon"></i>
          </button>

          <div class="user-dropdown" [class.show]="isUserMenuOpen">
            <div class="dropdown-header">
              <div class="user-info">
                <i class="fas fa-user-circle user-avatar"></i>
                <div class="user-details">
                  <span class="name">{{ (currentUser$ | async)?.userName }}</span>
                  <span class="email">{{ (currentUser$ | async)?.email }}</span>
                </div>
              </div>
            </div>

            <div class="dropdown-body">
              <a class="dropdown-item" routerLink="/account/manage">
                <i class="fas fa-user"></i>
                <span>{{ '::Layout:MyAccount' | abpLocalization }}</span>
              </a>
              <a class="dropdown-item" routerLink="/settings/two-factor-auth">
                <i class="fas fa-shield-alt"></i>
                <span>{{ '::Layout:TwoFactorAuth' | abpLocalization }}</span>
              </a>
              <a class="dropdown-item" routerLink="/setting-management">
                <i class="fas fa-cog"></i>
                <span>{{ '::Settings' | abpLocalization }}</span>
              </a>
            </div>

            <div class="dropdown-footer">
              <button class="logout-btn" (click)="onLogout()">
                <i class="fas fa-sign-out-alt"></i>
                <span>{{ '::Layout:Logout' | abpLocalization }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Login Button for non-authenticated users -->
        <div class="user-menu" *ngIf="!(currentUser$ | async)?.isAuthenticated">
          <button class="login-btn" (click)="navigateToLogin()">
            <i class="fas fa-sign-in-alt"></i>
            <span>{{ '::Layout:Login' | abpLocalization }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Page Content -->
    <main class="main-content">
      <!-- Router Outlet -->
      <router-outlet></router-outlet>
    </main>
  </div>

  <!-- Mobile Overlay -->
  <div class="mobile-overlay" *ngIf="isMobileMenuOpen" (click)="toggleMobileMenu()"></div>
</div>

<!-- ABP Components -->
<abp-confirmation></abp-confirmation>
